import { useEffect, useRef, type MouseEvent } from "react";
import { Link } from "react-router-dom";
import type { ProjectLink } from "@akhshy/types";
import { DESK_KIND_LABEL, type DeskItem } from "../../lib/deskItems.ts";

function DetailLink({ link }: { link: ProjectLink }) {
  if (link.url.startsWith("/")) {
    return (
      <Link className="home-detail-link" to={link.url}>
        {link.label}
      </Link>
    );
  }
  return (
    <a className="home-detail-link" href={link.url} target="_blank" rel="noopener">
      {link.label}
    </a>
  );
}

function DetailContent({ item }: { item: DeskItem }) {
  return (
    <>
      <span className="home-card-kind">{DESK_KIND_LABEL[item.kind]}</span>
      <h2 className="home-detail-title" id="home-detail-title">
        {item.title}
      </h2>
      {item.kind === "project" && <p className="home-detail-meta">{item.meta}</p>}
      {(item.kind === "hat" || item.kind === "project") && <p className="home-detail-body">{item.body}</p>}
      {item.kind === "stack" && (
        <ul className="home-detail-chips">
          {item.list.map((name) => (
            <li key={name}>{name}</li>
          ))}
        </ul>
      )}
      {item.kind === "project" && item.links.length > 0 && (
        <p className="home-detail-links">
          {item.links.map((link) => (
            <DetailLink key={link.url} link={link} />
          ))}
        </p>
      )}
    </>
  );
}

/**
 * Clicks on the backdrop target the <dialog> itself, but so do clicks on its own
 * padding, so only a click outside the dialog's box counts as the backdrop.
 */
function isBackdropClick(e: MouseEvent<HTMLDialogElement>): boolean {
  if (e.target !== e.currentTarget) return false;
  const box = e.currentTarget.getBoundingClientRect();
  return e.clientX < box.left || e.clientX > box.right || e.clientY < box.top || e.clientY > box.bottom;
}

/** Native modal dialog for a desk card; open while `item` is set. */
export function DeskDetailDialog({ item, onClose }: { item: DeskItem | null; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (item && !dialog.open) dialog.showModal();
    if (!item && dialog.open) dialog.close();
  }, [item]);

  return (
    <dialog
      ref={ref}
      className="home-detail"
      aria-labelledby="home-detail-title"
      onClose={onClose}
      onClick={(e) => {
        if (isBackdropClick(e)) e.currentTarget.close();
      }}
    >
      {item && <DetailContent item={item} />}
      <button type="button" className="home-detail-close" onClick={() => ref.current?.close()}>
        Close
      </button>
    </dialog>
  );
}
